/**
 * Sequence validation for scenario-group compositions.
 *
 * LEGACY: as of the single-slot Create rewrite, multi-slide groups are no
 * longer produced. This validator only fires from `validateLessonContent`
 * when an existing lesson contains a group of length >= 2 — i.e. it
 * guards legacy data on save. The opener/core/closer position rules and
 * verified pairings preserved here remain valid pedagogy and live on as
 * curriculum-level guidance (see KULA-CREATE-ARCHITECTURE.md), to be
 * reused if/when Designer-phase tooling recommends sequences across
 * multiple slots.
 *
 * Ported from kula-create `lib/compositor/validateSequence.ts`. Enforced at:
 *   - Designer step (warn during build; surface to user)
 *   - Server-side lesson save (reject hard errors)
 *
 * Rules:
 * 1. Length 2 or 3 (single-template builds skip this validator entirely).
 * 2. No repeated types.
 * 3. First slot can't be a `closer`; last slot can't be an `opener`.
 * 4. Middle of a 3-step sequence should be `core` (warning only).
 * 5. No `combosAvoid` pair anywhere in the sequence (hard error).
 * 6. Pairs without explicit `combosWellWith` linkage are warned, not rejected.
 */

import {
  SCENARIO_TEMPLATE_REGISTRY,
  type ScenarioTemplateId,
  type ScenarioTemplateMetadata,
} from './templateRegistry';

export interface SequenceValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validation mode.
 *
 * - 'strict' (default): hard rules (length, position, combosAvoid, duplicates)
 *   produce errors; soft suggestions produce warnings. Used at lesson save
 *   to keep persisted scenario groups well-formed.
 * - 'advisory': every hard rule downgrades to a warning. Used at the AI
 *   panel call site where a human is composing and may want to override —
 *   the panel shows the warnings but doesn't block the apply.
 *
 * The boolean `valid` only reflects errors. In advisory mode `valid` is
 * always true (because no errors exist); callers gate on warnings instead.
 */
export type ValidationMode = 'strict' | 'advisory';

export function validateScenarioSequence(
  types: string[],
  mode: ValidationMode = 'strict',
): SequenceValidationResult {
  const result = runValidation(types);
  if (mode === 'advisory') {
    return {
      valid: true,
      errors: [],
      warnings: [...result.errors, ...result.warnings],
    };
  }
  return result;
}

function runValidation(types: string[]): SequenceValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Rule 1: length
  if (types.length < 2 || types.length > 3) {
    errors.push(`Sequence must have 2-3 types, got ${types.length}`);
    return { valid: false, errors, warnings };
  }

  // Resolve entries (catch unknown types early)
  const entries: ScenarioTemplateMetadata[] = [];
  for (const type of types) {
    const entry = SCENARIO_TEMPLATE_REGISTRY[type as ScenarioTemplateId];
    if (!entry) {
      errors.push(`Unknown interaction type "${type}"`);
    } else {
      entries.push(entry);
    }
  }
  if (errors.length > 0) {
    return { valid: false, errors, warnings };
  }

  // Rule 2: no repeats
  const unique = new Set(types);
  if (unique.size !== types.length) {
    errors.push('Sequence contains duplicate types');
  }

  // Rule 3: position ordering
  const first = entries[0];
  const last = entries[entries.length - 1];

  if (first.position === 'closer') {
    errors.push(`"${first.id}" is a closer and cannot be first in the sequence`);
  }

  if (last.position === 'opener') {
    errors.push(`"${last.id}" is an opener and cannot be last in the sequence`);
  }

  // Rule 4: middle should be core in a 3-step (warn-only)
  if (entries.length === 3 && entries[1].position !== 'core') {
    warnings.push(
      `"${entries[1].id}" is a ${entries[1].position}, not core — unusual in middle position`,
    );
  }

  // Rule 5: combosAvoid violations
  for (let i = 0; i < entries.length; i++) {
    for (let j = 0; j < entries.length; j++) {
      if (i === j) continue;
      if (entries[i].combosAvoid.includes(entries[j].id)) {
        errors.push(`"${entries[i].id}" explicitly avoids pairing with "${entries[j].id}"`);
      }
    }
  }

  // Rule 6: combosWellWith suggestions (warn-only)
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const aRecommends = entries[i].combosWellWith.includes(entries[j].id);
      const bRecommends = entries[j].combosWellWith.includes(entries[i].id);
      if (!aRecommends && !bRecommends) {
        warnings.push(
          `"${entries[i].id}" and "${entries[j].id}" are not recommended pairs — may still work`,
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
